// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable, NgZone } from '@angular/core';
import { WasmHandle, WasmRuntimeBaseService } from '../runtime/wasm-runtime-base.service';
import { applyGlobals, GlobalMap } from '../utils/apply-globals.util';

@Injectable({ providedIn: 'root' })
export class AssembleViewWasmFacade {
  // ===== Fields =====
  private started = false;
  private handle: WasmHandle | null = null;
  private restoreGlobals: (() => void) | null = null;
  private restoreCallbacks: (() => void) | null = null;

  // ===== Constructor =====
  constructor(
    private base: WasmRuntimeBaseService,
    private zone: NgZone,
  ) {}

  // ===== Public API =====
  public async prepare(globals?: GlobalMap) {
    if (this.started) return;

    if (globals && Object.keys(globals).length > 0) {
      this.restoreGlobals = applyGlobals(globals);
    }

    try {
      this.handle = await this.base.createAndRun('wasm/assembleview.wasm');
      this.started = true;
    } catch (e) {
      this.restoreGlobals?.(); this.restoreGlobals = null;
      this.handle = null;
      this.started = false;
      throw e;
    }
  }

  public async waitForExports(
    names: string[] = ['readSVJSON', 'readAssemblyJSON'],
    timeoutMs = 10000
  ): Promise<void> {
    this.ensureReady();
    const start = Date.now();
    await this.zone.runOutsideAngular(() =>
      new Promise<void>((resolve, reject) => {
        const t = setInterval(() => {
          const ready = names.every(n => typeof (globalThis as any)[n] === 'function');
          if (ready) {
            clearInterval(t);
            resolve();
          } else if (Date.now() - start > timeoutMs) {
            clearInterval(t);
            reject(new Error(`Timed out waiting for WASM exports: ${names.join(', ')}`));
          }
        }, 30);
      })
    );
  }

  public registerCallback(handlers: {
    unBlock?: () => void;
  }) {
    const prevDescMap = new Map<string, PropertyDescriptor | undefined>();
    const hadOwnMap = new Map<string, boolean>();

    const define = (name: string, fn: Function | undefined) => {
      if (!fn) return;
      const hasOwn = Object.prototype.hasOwnProperty.call(globalThis, name);
      hadOwnMap.set(name, hasOwn);
      prevDescMap.set(name, hasOwn ? Object.getOwnPropertyDescriptor(globalThis, name) : undefined);
      Object.defineProperty(globalThis as any, name, {
        value: fn, configurable: true, enumerable: false, writable: true,
      });
    };

    define('unBlock', handlers.unBlock);

    this.restoreCallbacks = () => {
      for (const [name, desc] of prevDescMap.entries()) {
        const had = hadOwnMap.get(name);
        try {
          if (had && desc) Object.defineProperty(globalThis as any, name, desc);
          else delete (globalThis as any)[name];
        } catch {
          try { delete (globalThis as any)[name]; } catch {}
        }
      }
    };
  }

  public callReadSvJson(value: string) {
    this.callWasm('readSVJSON', value);
  }

  public callReadAssemblyJson(value: string) {
    this.callWasm('readAssemblyJSON', value);
  }

  public callRedraw() {
    this.callWasm('redraw');
  }

  public async dispose(): Promise<void> {
    try {
      // Intentionally no-op:
      // - The Go/WASM module has no exported disposer.
      // - Releasing references and restoring globals enables GC to collect.
    } catch {
      // Swallow any unexpected errors during teardown.
    }

    this.restoreCallbacks?.(); this.restoreCallbacks = null;
    this.restoreGlobals?.(); this.restoreGlobals = null;

    this.handle = null;
    this.started = false;
  }

  // ===== Private Helpers =====
  private ensureReady() {
    if (!this.started || !this.handle) {
      throw new Error('WASM is not prepared. Call prepare() first.');
    }
    if (!this.handle.isRunning()) {
      throw new Error('WASM runtime is not running.');
    }
  }

  private callWasm(name: string, ...args: unknown[]): unknown {
    this.ensureReady();
    const fn = this.handle!.getGlobal<(...vs: unknown[]) => unknown>(name);
    if (!fn) throw new Error(`${name} is not defined by WASM`);
    return fn(...args);
  }
}
